import { CreditCard, Loader2, Plus } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import Can from '../../../components/auth/Can';

import { RestaurantOrderBillingDTO } from '../../../services/restaurant/RestaurantOrderService';
import { RestaurantOrderStatus } from '../../../types/restaurant/RestaurantOrderTypes';

interface Props {
  orderStatus: RestaurantOrderStatus;
  billing: RestaurantOrderBillingDTO | null;
  billingMessage: string | null;
  loading: boolean;
  starting: boolean;
  reopening: boolean;
  onOpen: () => void;
  onAddMore: () => void;
  onContinue: () => void;
}

const RestaurantOrderBillingActions = ({
  orderStatus,
  billing,
  billingMessage,
  loading,
  starting,
  reopening,
  onOpen,
  onAddMore,
  onContinue,
}: Props) => {
  if (orderStatus === 'READY') {
    return (
      <div className="flex gap-2">
        <Can perm="RESTAURANT_ORDERS_OPEN">
          <ActionButton
            className="flex-1"
            label="Agregar más"
            icon={<Plus className="h-4 w-4" />}
            variant="outline"
            size="md"
            loading={reopening}
            loadingLabel="Abriendo..."
            disabled={reopening || starting}
            onClick={onAddMore}
          />
        </Can>
        <Can perm="RESTAURANT_ORDERS_CLOSE_BILL">
          <ActionButton
            className="flex-1"
            label="Cobrar"
            icon={<CreditCard className="h-4 w-4" />}
            variant="success"
            size="md"
            loading={starting}
            loadingLabel="Enviando..."
            disabled={starting || reopening}
            onClick={onOpen}
          />
        </Can>
      </div>
    );
  }

  if (orderStatus !== 'BILLING') return null;

  return (
    <div className="space-y-3">
      {loading && (
        <p className="inline-flex items-center gap-2 text-sm text-bodydark2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Recuperando cobro...
        </p>
      )}
      {billing?.status === 'PENDING' && billing.continuation && (
        <Can perm="RESTAURANT_ORDERS_CLOSE_BILL">
          <ActionButton
            label="Continuar cobro"
            icon={<CreditCard className="h-4 w-4" />}
            variant="primary"
            size="md"
            onClick={onContinue}
          />
        </Can>
      )}
      {billing?.status === 'FINALIZED' && (
        <p className="rounded-lg bg-warning/10 px-3 py-2.5 text-sm font-medium text-warning">
          Procesando documento
        </p>
      )}
      {billing?.status === 'ACCEPTED' && (
        <p className="text-sm text-bodydark2">Actualizando orden...</p>
      )}
      {(billing?.status === 'REJECTED' || billing?.status === 'CANCELLED') && (
        <div className="space-y-2">
          <p className="rounded-lg bg-danger/10 px-3 py-2.5 text-sm text-danger">
            {billingMessage ||
              (billing.status === 'REJECTED'
                ? 'El documento fue rechazado.'
                : 'El cobro fue cancelado.')}
          </p>
          <Can perm="RESTAURANT_ORDERS_CLOSE_BILL">
            <ActionButton
              label="Reintentar cobro"
              variant="primary"
              size="md"
              loading={starting}
              disabled={starting}
              onClick={onOpen}
            />
          </Can>
        </div>
      )}
    </div>
  );
};

export default RestaurantOrderBillingActions;
